import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

export interface SubjectOption {
  id: number;
  subject_name: string;
}

interface Props {
  /** Active, backend-provided subjects (id + subject_name). Never hardcoded. */
  subjects: SubjectOption[];
  /** Currently selected subject IDs. Empty array = "All Subjects" (no restriction). */
  value: number[];
  onChange: (ids: number[]) => void;
  /** Accepts a responsive object so the control can fill a grid cell. */
  width?: number | string | { [k: string]: number | string };
}

/**
 * Searchable multi-select Subject filter. Inclusive "Match Any" semantics live in
 * the backend query; this control only manages the selected subject-ID set.
 *
 * Collapsed display: "All Subjects" (none) · single name · two short names ·
 * "Subjects · N" (several). Menu stays open across selections, includes a search
 * box, Select All / Clear All, and a checkbox per subject.
 */
const SubjectMultiSelect: React.FC<Props> = ({ subjects, value, onChange, width = 160 }) => {
  const [search, setSearch] = useState("");

  const nameById = useMemo(() => {
    const m = new Map<number, string>();
    subjects.forEach((s) => m.set(Number(s.id), s.subject_name));
    return m;
  }, [subjects]);

  const filteredSubjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? subjects.filter((s) => String(s.subject_name || "").toLowerCase().includes(q))
      : subjects;
  }, [subjects, search]);

  const renderValue = (selected: number[]) => {
    if (!selected || selected.length === 0) return "All Subjects";
    if (selected.length === 1) return nameById.get(Number(selected[0])) ?? "1 subject";
    const names = selected
      .map((id) => nameById.get(Number(id)))
      .filter(Boolean) as string[];
    if (selected.length === 2 && names.join(", ").length <= 20) return names.join(", ");
    return `Subjects · ${selected.length}`;
  };

  const allIds = useMemo(() => subjects.map((s) => Number(s.id)), [subjects]);

  return (
    <FormControl size="small" sx={{ width }}>
      <InputLabel shrink>Subject</InputLabel>
      <Select
        multiple
        notched
        displayEmpty
        label="Subject"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          onChange(typeof v === "string" ? [] : (v as number[]).map(Number));
        }}
        renderValue={(selected) => renderValue(selected as number[])}
        sx={{ borderRadius: "10px" }}
        MenuProps={{
          autoFocus: false,
          PaperProps: { style: { maxHeight: 360, width: 280 } },
        }}
      >
        {/* Search + bulk actions — a non-selectable header so it never enters value */}
        <ListSubheader sx={{ bgcolor: "background.paper", px: 1, pt: 1, pb: 0.5 }}>
          <TextField
            size="small"
            autoFocus
            fullWidth
            placeholder="Search subjects…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, mr: 0.5, color: "text.disabled" }} /> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
            <Button size="small" onClick={() => onChange(allIds)} sx={{ textTransform: "none", fontSize: 11.5, minWidth: 0 }}>
              Select All
            </Button>
            <Button size="small" onClick={() => onChange([])} sx={{ textTransform: "none", fontSize: 11.5, minWidth: 0 }} disabled={value.length === 0}>
              Clear All
            </Button>
          </Box>
        </ListSubheader>

        {filteredSubjects.length === 0 && (
          <MenuItem disabled>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>No subjects found</Typography>
          </MenuItem>
        )}

        {filteredSubjects.map((s) => {
          const id = Number(s.id);
          return (
            <MenuItem key={id} value={id} sx={{ py: 0.25 }}>
              <Checkbox size="small" checked={value.includes(id)} sx={{ py: 0.25, mr: 0.5 }} />
              <ListItemText primary={s.subject_name} primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default SubjectMultiSelect;
